import authService from './authService';

// ─── Types ──────────────────────────────────────────────────────

export interface PurchaseReportFilters {
  date_from: string;
  date_to: string;
  provider_id?: number;
  page: number;
  pageSize: number;
}

export interface PurchaseReportRow {
  product_id: number;
  sku: string;
  description: string;
  total_quantity: number;
  total_delivered_quantity: number;
  purchase_order_ids: number[];
}

export interface PurchaseReportSummary {
  total_products: number;
  total_quantity: number;
  total_delivered_quantity: number;
}

export interface PurchaseReportResponse {
  data: PurchaseReportRow[];
  summary: PurchaseReportSummary;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// ─── Service ────────────────────────────────────────────────────

class PurchaseReportService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  private async getBaseUrl(): Promise<string> {
    const { getConfig } = await import('../config');
    return getConfig().api.baseUrl;
  }

  async getReport(filters: PurchaseReportFilters): Promise<PurchaseReportResponse> {
    const params = new URLSearchParams({
      page: filters.page.toString(),
      limit: filters.pageSize.toString(),
      date_from: filters.date_from,
      date_to: filters.date_to,
    });

    if (filters.provider_id !== undefined) {
      params.append('provider_id', filters.provider_id.toString());
    }

    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/reports/purchases?${params}`);
    if (!response.ok) throw new Error('Erro ao carregar relatório de compras');

    const json = await response.json();

    const items: PurchaseReportRow[] = Array.isArray(json.data) ? json.data : [];
    const pg = json.pagination ?? {};
    const summary: PurchaseReportSummary = json.summary ?? {
      total_products: 0,
      total_quantity: 0,
      total_delivered_quantity: 0,
    };

    return {
      data: items,
      summary,
      pagination: {
        page: pg.page ?? filters.page,
        limit: pg.page_size ?? pg.pageSize ?? filters.pageSize,
        total: pg.total_items ?? pg.total ?? items.length,
        totalPages: pg.total_pages ?? pg.totalPages ?? (Math.ceil((pg.total_items ?? pg.total ?? items.length) / filters.pageSize) || 1),
      },
    };
  }

  async downloadPdf(filters: Omit<PurchaseReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.provider_id !== undefined) {
      params.append('provider_id', filters.provider_id.toString());
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/purchases/pdf?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar PDF (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-compras.pdf`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }

  async downloadExcel(filters: Omit<PurchaseReportFilters, 'page' | 'pageSize'>): Promise<void> {
    const baseUrl = await this.getBaseUrl();

    const params = new URLSearchParams({
      date_from: filters.date_from,
      date_to: filters.date_to,
    });
    if (filters.provider_id !== undefined) {
      params.append('provider_id', filters.provider_id.toString());
    }

    const doFetch = async (token?: string | null) =>
      fetch(`${baseUrl}/reports/purchases/excel?${params}`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    let response = await doFetch();
    if (response.status === 401) {
      const token = await authService.getAuthToken(false);
      response = await doFetch(token);
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(body.message || `Erro ao gerar Excel (${response.status})`);
    }

    const disposition = response.headers.get('content-disposition') ?? '';
    const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
    const filename = filenameMatch?.[1] ?? `relatorio-compras.xlsx`;

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}

export const purchaseReportService = new PurchaseReportService();
export default purchaseReportService;
